import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import { notify, useAppStore } from "@/store/store";
import { fromApiZone, toApiZoneCreate, toApiZoneUpdate } from "@/lib/adapters";
import type { ApiZone, PaginatedResponse } from "@/types/api";
import type { ZoneConfig } from "@/store/types";

const ZONE_FIELDS = [
  "id",
  "name",
  "color",
  "border_color",
  "layout",
  "is_block",
  "is_available",
  "reservation_mode",
  "public_bookable_weekdays",
  "created_at",
] as const satisfies readonly (keyof ApiZone)[];

// ── Queries ──────────────────────────────────────────────────────

export function useZones(locationId?: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const storeLocationId = useAuthStore((s) => s.locationId);

  const query = useQuery({
    queryKey: ["zones", organizationId, locationId ?? storeLocationId],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: ZONE_FIELDS.join(",") });
      if (locationId) params.set("location", locationId);
      const res = await api.get(`zones/?${params}`).json<PaginatedResponse<ApiZone>>();
      return res.items
        .toSorted((a, b) => a.created_at.localeCompare(b.created_at))
        .map(fromApiZone);
    },
    enabled: isAuthenticated && !!organizationId && !!storeLocationId,
  });

  // Sync to store — already sorted by created_at from queryFn
  useEffect(() => {
    if (query.data) {
      useAppStore.setState({ zones: query.data });
    }
  }, [query.data]);

  return query;
}

// ── Mutations ────────────────────────────────────────────────────

export function useCreateZone() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: (data: { zone: ZoneConfig }) =>
      api.post("zones/", { json: toApiZoneCreate(data.zone) }).json<ApiZone>(),
    onSuccess: (apiZone, { zone: localZone }) => {
      // Replace local zone with API version (correct UUID)
      const mapped = fromApiZone(apiZone);
      useAppStore.setState((state) => ({
        zones: state.zones.map((z) => (z.id === localZone.id ? mapped : z)),
      }));
      queryClient.invalidateQueries({ queryKey: ["zones", organizationId] });
    },
  });
}

export function useUpdateZone() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<ZoneConfig> }) =>
      api.patch(`zones/${id}/`, { json: toApiZoneUpdate(changes) }).json<ApiZone>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zones", organizationId] });
    },
    onError: (err) => {
      notify("error", "No se pudo guardar la zona", err instanceof Error ? err.message : "");
    },
  });
}

export function useDeleteZone() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: (id: string) => api.delete(`zones/${id}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zones", organizationId] });
      queryClient.invalidateQueries({ queryKey: ["tables", organizationId] });
    },
  });
}
